import { reducer as notifications } from 'react-notification-system-redux';
import { RouterState } from 'react-router-redux';
import { combineReducers } from 'redux';
import { reducer as formReducer } from 'redux-form';

import { authReducer } from '@topconagriculture/auth';
import { AuthState } from '@topconagriculture/auth/build/store/auth.reducer';
import { profilingReducer, ProfilingState } from '@topconagriculture/profiling';
import { loaderReducer, LoaderState } from '@topconagriculture/loader';
import { modalReducer, ModalState } from '@topconagriculture/modal';
import { countryReducer, CountryState } from '@topconagriculture/country';
import { languageReducer, LanguageState } from '@topconagriculture/language';

import appFeedGroupsReducer, { AppFeedGroupState } from './application/groupPreview/groupPreview.reducer';
import loadMixersReducer, { LoadMixersState } from './application/loadMixers/loadMixers.reducer';
import appPremixReducer, { AppPremixState } from './application/premixes/appPremixes.reducer';
import appRecipeReducer, { AppRecipeState } from './application/recipes/appRecipes.reducer';
import appIngredientsReducer, { appIngredientsState } from './application/ingredients/appIngredients.reducer';
import appReportsReducer, { AppReportsState } from './application/reports/appReports.reducer';
import { routerReducer } from './config/history';
import codeReducer, { CodeState } from './entities/code/code.reducer';
import farmsReducer, { FarmsState } from './entities/farms/farms.reducer';
import feedingReducer, { FeedingState } from './entities/feeding/feeding.reducer';
import groupReducer, { GroupState } from './entities/group/group.reducer';
import groupTypeReducer, { GroupTypeState } from './entities/groupType/groupType.reducer';
import ingredientsReducer, { IngredientState } from './entities/ingredients/ingredients.reducer';
import mixersReducer, { MixersState } from './entities/mixers/mixers.reducer';
import premixesReducer, { PremixState } from './entities/premixes/premixes.reducer';
import recipesReducer, { RecipeState } from './entities/recipes/recipes.reducer';
import reportsReducer, { ReportsState } from './entities/reports/reports.reducer';
import scaleTypeReducer, { ScaleTypeState } from './entities/scaleType/scaleType.reducer';
import sharingReducer, { SharingState } from './entities/sharing/sharing.reducer';
import selectedReducer, { SelectedState } from './selected/selected.reducer';
import operatorsReducer, { OperatorState } from './entities/operators/operators.reducer';
import {
  feedGroupsReducerContainer,
  feedingListFormIsChangedReducerContainer,
  feedingListVisitedReducerContainer,
  preventNavigationReducerContainer,
  sideBarsReducerContainer,
  tabsReducerContainer,
  UIState,
} from './ui/ui.reducer';
import userReducer, { UserState } from './user/user.reducer';
import presetReducer, { PresetState } from './entities/preset/preset.reducer';
import settingsReduser, { SettingsState } from './entities/settings/settings.reducer';
import bootstrapReducer, { BoostrapState } from './application/bootstrap/bootstrap.reducer';
import dataToolsReducer, { DataToolsState } from './entities/dataTools/dataTools.reducer';
import zonesReducer, { ZonesState } from './entities/zones/zones.reducer';
import companyReducer, { CompanyState } from './entities/company/company.reducer';
import companyUserReducer, { CompanyUserState } from './entities/companyUser/company.user.reducer';
import commonReducer, { CommonState } from './application/common/common.reducer';
import subscriptionReducer, { SubscriptionState } from './subscription/subscription.reducer';
import refusalsReducer, { RefusalsState } from './entities/refusals/refusals.reducer';
import lotsReducer, { LotsState } from './entities/lots/lots.reducer';
import relationsReducer, { RelationsState } from './entities/relations/relations.reducer';
import lotTypesReducer, { LotTypesState } from './entities/lotTypes/lotTypes.reducer';
import lotMarketTypesReducer, { LotMarketTypesState } from './entities/lotMarketTypes/lotMarketTypes.reducer';
import appLotsReducer, { AppLotsState } from './application/lots/appLots.reducer';
import hmsReducer, { HerdManagementSystemState } from './entities/hms/hms.reducer';
import inventoryReducer from './entities/inventory/inventory.reducer';
import automatedReportsReducer, { AutomatedReportsState } from './entities/automatedReports/automatedReports.reducer';
import ordersReducer, { OrderState } from './entities/orders/orders.reducer';
import appOrderReducer, { AppOrdersState } from './application/ingredientsInOrder/ingredientsInOrder.reducer';
import weighbridgeReducer, { WeighbridgeState } from './entities/weighbridge/weighbridge.reducer';
import dashboardReducer, { DashboardState } from './entities/dashboard/dashboard.reducer';
import costTypesReducer, { CostTypesState } from './entities/costTypes/costTypes.reducer';
import pdfReducer, { PDFState } from './application/pdf/pdf.reducer';
import driverPermissionsReducer, {
  DriverPermissionsState,
} from './entities/driverPermissions/driverPermissions.reducer';
import locationsReducer, { LocationState } from './entities/locations/locations.reducer';
import binControlReducer, { BinControlState } from './entities/binControl/binControl.reducer';

export interface RootState extends ProfilingState, AuthState, CountryState, LanguageState {
  user: UserState;
  router: RouterState;
  entities: {
    ingredients: IngredientState;
    companies: CompanyState;
    companyUser: CompanyUserState;
    dashboard: DashboardState;
    recipes: RecipeState;
    premixes: PremixState;
    farms: FarmsState;
    groupType: GroupTypeState;
    group: GroupState;
    code: CodeState;
    feeding: FeedingState;
    mixers: MixersState;
    scaleTypes: ScaleTypeState;
    reports: ReportsState;
    presets: PresetState;
    sharing: SharingState;
    operators: OperatorState;
    orders: OrderState;
    settings: SettingsState;
    dataTools: DataToolsState;
    zones: ZonesState;
    refusals: RefusalsState;
    lots: LotsState;
    relations: RelationsState;
    lotTypes: LotTypesState;
    lotMarketTypes: LotMarketTypesState;
    costTypes: CostTypesState;
    hms: HerdManagementSystemState;
    inventory: any;
    automatedReports: AutomatedReportsState;
    weighbridgeTickets: WeighbridgeState;
    driverPermissions: DriverPermissionsState;
    locations: LocationState;
    binControl: BinControlState;
  };
  selected: SelectedState;
  application: {
    common: CommonState;
    feedGroups: AppFeedGroupState;
    recipes: AppRecipeState;
    loadMixers: LoadMixersState;
    premixes: AppPremixState;
    ingredients: appIngredientsState;
    reports: AppReportsState;
    bootstrap: BoostrapState;
    lots: AppLotsState;
    orders: AppOrdersState;
    pdf: PDFState;
  };
  form: any;
  ui: UIState & LoaderState & ModalState;
  notifications: any;
  subscription: SubscriptionState;
}

// A typical application will likely have a mixture of relational data and non-relational data.
// While there is no single rule for exactly how those different types of data should be organized,
// one country pattern is to put the relational "tables" under a country parent key, such as "entities"
// http://redux.js.org/docs/recipes/reducers/NormalizingStateShape.html
const entities = combineReducers({
  ingredients: ingredientsReducer,
  companies: companyReducer,
  companyUser: companyUserReducer,
  dashboard: dashboardReducer,
  farms: farmsReducer,
  premixes: premixesReducer,
  recipes: recipesReducer,
  groupType: groupTypeReducer,
  group: groupReducer,
  code: codeReducer,
  feeding: feedingReducer,
  mixers: mixersReducer,
  scaleTypes: scaleTypeReducer,
  reports: reportsReducer,
  presets: presetReducer,
  sharing: sharingReducer,
  operators: operatorsReducer,
  orders: ordersReducer,
  settings: settingsReduser,
  dataTools: dataToolsReducer,
  zones: zonesReducer,
  refusals: refusalsReducer,
  lots: lotsReducer,
  relations: relationsReducer,
  lotTypes: lotTypesReducer,
  lotMarketTypes: lotMarketTypesReducer,
  costTypes: costTypesReducer,
  hms: hmsReducer,
  inventory: inventoryReducer,
  automatedReports: automatedReportsReducer,
  weighbridgeTickets: weighbridgeReducer,
  driverPermissions: driverPermissionsReducer,
  locations: locationsReducer,
  binControl: binControlReducer,
});

const application = combineReducers({
  common: commonReducer,
  feedGroups: appFeedGroupsReducer,
  recipes: appRecipeReducer,
  loadMixers: loadMixersReducer,
  premixes: appPremixReducer,
  reports: appReportsReducer,
  ingredients: appIngredientsReducer,
  bootstrap: bootstrapReducer,
  lots: appLotsReducer,
  orders: appOrderReducer,
  pdf: pdfReducer,
});

const ui = combineReducers({
  ...loaderReducer,
  ...modalReducer,
  ...tabsReducerContainer,
  ...preventNavigationReducerContainer,
  ...feedGroupsReducerContainer,
  ...feedingListVisitedReducerContainer,
  ...feedingListFormIsChangedReducerContainer,
  ...sideBarsReducerContainer,
});

const rootReducer = combineReducers<RootState>({
  ...profilingReducer,
  ...authReducer,
  ...countryReducer,
  ...languageReducer,
  user: userReducer,
  router: routerReducer,
  entities,
  form: formReducer, // https://redux-form.com
  application,
  selected: selectedReducer,
  ui,
  notifications,
  subscription: subscriptionReducer,
});

export default rootReducer;
